import { DatePicker } from "@shared/ui";
import { InputNumber } from "@shared/ui/InputNumber";
import dayjs, { Dayjs } from "dayjs";
import { useEffect, useState } from "react";
import {
  clampFrequencyHours,
  DEFAULT_UPDATE_FREQUENCY_HOURS,
  MAX_UPDATE_FREQUENCY_HOURS,
  MIN_UPDATE_FREQUENCY_HOURS,
  type ReportUpdateConfigValue,
} from "../../lib/utils/reportUpdateConfig";
import styles from "./styles.module.scss";

interface ReportUpdateConfigFieldsProps {
  value: ReportUpdateConfigValue;
  onChange: (next: ReportUpdateConfigValue) => void;
  /** Stack fields vertically (waiting-state / compact layouts) */
  compact?: boolean;
}

export function ReportUpdateConfigFields({
  value,
  onChange,
  compact = false,
}: ReportUpdateConfigFieldsProps) {
  const [draftFrequency, setDraftFrequency] = useState<number | null>(
    value.updateForIntervalHourFrequency
  );

  useEffect(() => {
    setDraftFrequency(value.updateForIntervalHourFrequency);
  }, [value.updateForIntervalHourFrequency]);

  const handleDateChange = (date: Dayjs | null) => {
    onChange({
      ...value,
      updateTillDate: date ? date.format("YYYY-MM-DD") : "",
    });
  };

  const handleFrequencyChange = (hours: number | string | null) => {
    if (hours === null || hours === "") {
      setDraftFrequency(null);
      return;
    }

    const parsed = typeof hours === "string" ? Number(hours) : hours;
    if (Number.isNaN(parsed)) {
      setDraftFrequency(null);
      return;
    }

    setDraftFrequency(parsed);

    if (
      parsed >= MIN_UPDATE_FREQUENCY_HOURS &&
      parsed <= MAX_UPDATE_FREQUENCY_HOURS
    ) {
      onChange({
        ...value,
        updateForIntervalHourFrequency: parsed,
      });
    }
  };

  const handleFrequencyBlur = () => {
    const clamped = clampFrequencyHours(
      draftFrequency ?? DEFAULT_UPDATE_FREQUENCY_HOURS
    );
    setDraftFrequency(clamped);
    onChange({
      ...value,
      updateForIntervalHourFrequency: clamped,
    });
  };

  return (
    <div
      className={`${styles.fieldsGrid}${compact ? ` ${styles.fieldsGridCompact}` : ""}`}
    >
      <div className={styles.field}>
        <label className={styles.fieldLabel} htmlFor="report-update-till">
          Update till
        </label>
        <DatePicker
          id="report-update-till"
          value={value.updateTillDate ? dayjs(value.updateTillDate) : null}
          onChange={handleDateChange}
          disabledDate={(current) =>
            !!current && current.isBefore(dayjs().startOf("day"))
          }
          format="DD MMM YYYY"
          allowClear={false}
          className={styles.datePicker}
          placeholder="Select date"
        />
        <p className={styles.helperText}>
          Report metrics keep updating until this date.
        </p>
      </div>

      <div className={styles.field}>
        <label className={styles.fieldLabel} htmlFor="report-frequency">
          Frequency
        </label>
        <div className={styles.frequencyInputWrap}>
          <InputNumber
            id="report-frequency"
            step={1}
            precision={0}
            value={draftFrequency}
            onChange={handleFrequencyChange}
            onBlur={handleFrequencyBlur}
            className={styles.frequencyInput}
          />
          <span className={styles.frequencySuffix}>hours</span>
        </div>
        <p className={styles.helperText}>
          How often to refresh · {MIN_UPDATE_FREQUENCY_HOURS}–
          {MAX_UPDATE_FREQUENCY_HOURS} hrs (default{" "}
          {DEFAULT_UPDATE_FREQUENCY_HOURS}).
        </p>
      </div>
    </div>
  );
}
